import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '../api/context.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { useSession } from '../auth/session-context.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';

interface Intake {
  id: string;
  channel_id: string;
  name: string;
  enabled: boolean;
  url: string;
  email: string | null;
}
interface IntakeList {
  endpoints: Intake[];
  channels: { id: string; name: string }[];
}
type Operation = 'rotate' | 'stop' | 'resume' | 'remove';
const labels: Record<Operation, string> = {
  rotate: '作り直す',
  stop: '止める',
  resume: '再開する',
  remove: '消す',
};

export function IntakesPage(): ReactNode {
  const { profile } = useSession();
  return profile?.me.role === 'hq_admin' ? (
    <IntakesContent />
  ) : (
    <ErrorBlock message="この画面はお使いいただけません。" />
  );
}

function IntakesContent(): ReactNode {
  const api = useApi();
  const [data, setData] = useState<IntakeList | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reload, setReload] = useState(0);
  const [channel, setChannel] = useState('');
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState<{ intake: Intake; operation: Operation } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    api
      .get<IntakeList>('/admin/intakes')
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reload]);

  async function save(action: () => Promise<unknown>, message: string): Promise<void> {
    setBusy(true);
    setResult(null);
    try {
      await action();
      setResult({ tone: 'success', message });
      setEditing(null);
      setConfirming(null);
      setReload((n) => n + 1);
    } catch (caught) {
      setResult({ tone: 'error', message: loadFailure(caught).message });
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  }

  async function copy(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setResult({ tone: 'success', message: 'コピーしました。' });
    } catch {
      setResult({
        tone: 'error',
        message: 'コピーできませんでした。宛先の欄を選んでコピーしてください。',
      });
    }
  }

  function perform(): void {
    if (!confirming) return;
    const { intake, operation } = confirming;
    const path = `/admin/intakes/${intake.id}`;
    void save(
      () =>
        operation === 'remove'
          ? api.del(path)
          : operation === 'rotate'
            ? api.post(`${path}/rotate`)
            : api.patch(path, { body: { enabled: operation === 'resume' } }),
      operation === 'rotate'
        ? '宛先を作り直しました。前の宛先は使えません。'
        : operation === 'remove'
          ? '受け口を消しました。過去のメッセージは残ります。'
          : operation === 'stop'
            ? '受け口を止めました。'
            : '受け口を再開しました。',
    );
  }

  return (
    <>
      <PageHeader
        title="自動メッセージの受け口"
        description="ほかのシステムからのお知らせを、選んだトークに流します。"
      />
      {result ? <ResultBanner {...result} reveal /> : null}
      {error ? <ErrorBlock failure={error} onRetry={() => setReload((n) => n + 1)} /> : null}
      {!data && !error ? <LoadingBlock label="受け口を読み込んでいます…" /> : null}
      {data ? (
        <>
          <p>
            宛先を知っている方はメッセージを送れます。送りたいシステムだけにお伝えください。添付ファイルは載りません。
          </p>
          <FormCard
            title="受け口を作る"
            submitLabel="作る"
            busy={busy}
            onSubmit={() => {
              if (!channel) {
                setResult({ tone: 'error', message: 'トークを選んでください。' });
                return;
              }
              void save(
                () => api.post('/admin/intakes', { body: { channel_id: channel, name } }),
                '受け口を作りました。',
              );
            }}
          >
            <label className="ui-field">
              <span>送り先のトーク</span>
              <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                <option value="">選んでください</option>
                {data.channels.map((talk) => (
                  <option key={talk.id} value={talk.id}>
                    {talk.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="ui-field">
              <span>受け口の名前（空でもかまいません）</span>
              <input maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          </FormCard>
          {data.endpoints.length === 0 ? <EmptyBlock label="受け口はまだありません。" /> : null}
          {data.endpoints.map((intake) => {
            const url = new URL(intake.url, window.location.origin).href;
            return (
              <Card
                key={intake.id}
                title={intake.name || 'システム通知'}
                description={`${data.channels.find((talk) => talk.id === intake.channel_id)?.name ?? '利用できないトーク'}・${intake.enabled ? '受け取り中' : '停止中'}`}
              >
                {intake.email ? (
                  <label className="ui-field">
                    <span>メールアドレス</span>
                    <input readOnly value={intake.email} />
                    <Button variant="secondary" onClick={() => void copy(intake.email!)}>
                      メールアドレスをコピー
                    </Button>
                  </label>
                ) : null}
                <label className="ui-field">
                  <span>送信用のURL</span>
                  <input readOnly value={url} />
                  <Button variant="secondary" onClick={() => void copy(url)}>
                    URLをコピー
                  </Button>
                </label>
                <p>GASから送る例（件名か本文のどちらかを入れます）</p>
                <pre>{`UrlFetchApp.fetch(${JSON.stringify(url)}, {\n  method: 'post', contentType: 'application/json',\n  payload: JSON.stringify({subject: 'お知らせ', body: '本文'})\n});`}</pre>
                {editing?.id === intake.id ? (
                  <FormCard
                    bare
                    submitLabel="名前を保存する"
                    busy={busy}
                    onCancel={() => setEditing(null)}
                    onSubmit={() =>
                      void save(
                        () =>
                          api.patch(`/admin/intakes/${intake.id}`, {
                            body: { name: editing.name },
                          }),
                        '受け口の名前を変えました。',
                      )
                    }
                  >
                    <label className="ui-field">
                      <span>新しい名前</span>
                      <input
                        maxLength={100}
                        value={editing.name}
                        onChange={(e) => setEditing({ id: intake.id, name: e.target.value })}
                      />
                    </label>
                  </FormCard>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => setEditing({ id: intake.id, name: intake.name })}
                  >
                    名前を変える
                  </Button>
                )}
                {(['rotate', intake.enabled ? 'stop' : 'resume', 'remove'] as Operation[]).map(
                  (operation) => (
                    <Button
                      key={operation}
                      variant={
                        operation === 'stop' || operation === 'remove' ? 'danger' : 'secondary'
                      }
                      disabled={busy}
                      onClick={() => setConfirming({ intake, operation })}
                    >
                      {labels[operation]}
                    </Button>
                  ),
                )}
              </Card>
            );
          })}
        </>
      ) : null}
      <ConfirmDialog
        open={confirming !== null}
        title={confirming ? `受け口を${labels[confirming.operation]}` : ''}
        message={
          confirming?.operation === 'rotate'
            ? 'メールアドレスとURLを作り直します。送り元の設定を新しい宛先に直してください。'
            : confirming?.operation === 'remove'
              ? 'この受け口への受け取りを終えます。過去のメッセージは残ります。'
              : confirming?.operation === 'stop'
                ? '止めている間に届いたお知らせは捨てられます。'
                : '同じ宛先で受け取りを再開します。'
        }
        reversibility={
          confirming?.operation === 'rotate' || confirming?.operation === 'remove'
            ? '前の宛先には戻せません。'
            : 'あとから切り替えられます。'
        }
        reversible={confirming?.operation === 'stop' || confirming?.operation === 'resume'}
        tone={confirming?.operation === 'resume' ? 'primary' : 'danger'}
        confirmLabel={confirming ? labels[confirming.operation] : ''}
        busy={busy}
        onConfirm={perform}
        onCancel={() => setConfirming(null)}
      />
    </>
  );
}
